import { z } from "zod";
import { channelFeedRelatedArtifactsSchema } from "./feed-artifacts.js";
import { channelFeedEngagementSchema } from "./feed-engagement.js";
import { channelObjectFieldTypeSchema } from "./channel-field-types.js";
/*
 * Workspace feed: a per-user envelope over workspace_events plus routed
 * actionable items (mentions first). The envelope carries identity, routing
 * and state; item bodies are hydrated from the canonical resources at read
 * time so the feed can never drift from the serializers (decision 0002).
 */
export const feedItemKindSchema = z.enum([
    "channel",
    "post",
    "record",
    "view",
    "file",
    "session",
    "schema",
    "mention",
    "automation",
    "approval",
]);
export const feedItemActionSchema = z.enum([
    "open",
    "reply",
    "mark_read",
    "mark_handled",
    "dismiss",
    "retry",
]);
export const feedItemStateSchema = z.enum(["open", "handled", "dismissed"]);
export const feedActorSchema = z.object({
    id: z.string(),
    type: z.enum(["user", "agent", "service", "unknown"]),
    /* Human identity behind the actor when known (plugin subject) — the id
       surfaces resolve to a display name via the org member directory. */
    subjectId: z.string().nullable(),
    clientApp: z.string().nullable(),
    interfaceType: z.string().nullable(),
    /* True when a governed Twin run executed the mutation on the human's
       behalf. The human remains the primary actor. */
    executedByTwin: z.boolean().default(false),
    /* A tenant API key that acted without a member is its own principal:
       its label, so surfaces name the key instead of an opaque id. */
    apiKeyLabel: z.string().trim().min(1).nullish(),
});
export const feedRecordChangeSchema = z.object({
    fieldKey: z.string().trim().min(1),
    label: z.string().trim().min(1),
    fieldType: channelObjectFieldTypeSchema.nullable(),
    changeKind: z.enum(["added", "updated", "removed"]),
    previousValue: z.unknown(),
    value: z.unknown(),
}).strict();
export const feedRecordDetailsSchema = z.object({
    title: z.string().trim().min(1),
    changes: z.array(feedRecordChangeSchema).max(2),
    hiddenChangeCount: z.number().int().nonnegative(),
    /* The Data Set the record belongs to, so feed rows and record cards can
     * name where it lives; older payloads omit it. */
    dataSetId: z.string().trim().min(1).nullish(),
    dataSetName: z.string().trim().min(1).nullish(),
}).strict();
export const feedMentionReferenceSchema = z.object({
    id: z.string().trim().min(1),
    type: z.enum(["user", "channel", "dataSet", "view", "entity"]),
    /* User display names come from the caller's organisation directory; entity names from the reader's data access. */
    label: z.string().trim().min(1).nullable(),
    /* Data Sets and views link through the channel that owns them. */
    channelId: z.string().trim().min(1).nullable(),
}).strict();
export const feedItemSchema = z.object({
    /*
     * Stable key: `event:<seq>` for stream items, `mention:<postId>` for routed
     * actionable items. State writes reference this key.
     */
    key: z.string().min(1),
    kind: feedItemKindSchema,
    seq: z.number().int().nullable(),
    occurredAt: z.number().int(),
    operation: z.enum(["upsert", "delete"]),
    channelId: z.string().nullable(),
    channelName: z.string().nullable(),
    resourceType: z.string(),
    resourceId: z.string(),
    actor: feedActorSchema,
    engagement: channelFeedEngagementSchema.optional(),
    /* One-line human description hydrated from the resource. */
    summary: z.string(),
    /* Structured record changes preserve field types for rich values such as people. */
    record: feedRecordDetailsSchema.optional(),
    /* Present for kinds with useful bodies (post/mention body excerpt). */
    preview: z.string().nullable(),
    /* What a finished run made or changed: its canvases, documents and links, as that run left them. */
    artifacts: channelFeedRelatedArtifactsSchema.optional(),
    /* Structured references keep typed mentions intact outside channel threads. */
    mentions: z.array(feedMentionReferenceSchema).max(80),
    actionable: z.boolean(),
    actions: z.array(feedItemActionSchema),
    /* Only actionable items carry mutable state; stream items are read-only. */
    state: feedItemStateSchema.nullable(),
    /* Canonical outbound items carry independent read state; stream rows use the cursor. */
    readAt: z.number().int().nullable().optional(),
    /* Set when the item is routed to one user (e.g. the mentioned user). */
    requiresUserId: z.string().nullable(),
    /* Kind-specific references (e.g. automationId for retry). */
    context: z.record(z.string(), z.string()).nullable(),
});
export const feedListResponseSchema = z.object({
    items: z.array(feedItemSchema),
    /* Highest seq the tenant has issued; poll/cursor anchor. */
    latestSeq: z.number().int(),
    /* The caller's stored read cursor (0 when never set). */
    readCursorSeq: z.number().int(),
    /* Pass as sinceSeq to page older items; null when exhausted. */
    nextBeforeSeq: z.number().int().nullable(),
    openActionableCount: z.number().int(),
    unreadActionableCount: z.number().int(),
    /*
     * How many action proposals are waiting for this person. The feed only
     * points at them: deciding happens in the Twin proposals surface, so
     * catching up never turns into authorising by reflex.
     */
    openActionCount: z.number().int().optional(),
});
export const feedMarkReadRequestSchema = z.object({
    /* Advance-only and capped at the tenant's current event tip. */
    seq: z.number().int().min(0),
    /* Optional stable reader identity. Item handled/dismissed state remains user-scoped. */
    consumerId: z.string().trim().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,119}$/u).optional(),
});
export const feedMarkReadResponseSchema = z.object({
    lastSeenSeq: z.number().int().min(0),
}).strict();
export const feedExpandRequestSchema = z.object({
    keys: z.array(z.string().trim().min(1)).min(1).max(5),
}).strict();
export const feedExpandResponseSchema = z.object({
    items: z.array(z.object({
        key: z.string().trim().min(1),
        resource: z.record(z.string(), z.unknown()).nullable(),
    }).strict()),
}).strict();
export const feedItemStateRequestSchema = z.object({
    key: z.string().min(1),
    state: z.enum(["handled", "dismissed", "open"]),
});
export const feedItemStateResponseSchema = feedItemStateRequestSchema.strict();
